import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

export type SoapNotePlanItem = {
  key: string
  text: string
}

interface SoapNotePlanContextValue {
  selectedPlanItems: readonly SoapNotePlanItem[]
  addToPlan: (item: SoapNotePlanItem) => boolean
  removeFromPlan: (key: string) => void
  clearPlanItems: () => void
  isInPlan: (key: string) => boolean
}

const SoapNotePlanContext = createContext<SoapNotePlanContextValue | null>(null)

export function SoapNotePlanProvider({ children }: { children: ReactNode }) {
  const [selectedPlanItems, setSelectedPlanItems] = useState<SoapNotePlanItem[]>([])
  const selectedPlanItemKeysRef = useRef<Set<string>>(new Set())

  const addToPlan = useCallback((item: SoapNotePlanItem) => {
    const key = item.key.trim()
    const text = item.text.trim()

    if (!key || !text || selectedPlanItemKeysRef.current.has(key)) {
      return false
    }

    selectedPlanItemKeysRef.current.add(key)
    setSelectedPlanItems((current) => [...current, { key, text }])
    return true
  }, [])

  const removeFromPlan = useCallback((key: string) => {
    const normalizedKey = key.trim()
    selectedPlanItemKeysRef.current.delete(normalizedKey)
    setSelectedPlanItems((current) => current.filter((item) => item.key !== normalizedKey))
  }, [])

  const clearPlanItems = useCallback(() => {
    selectedPlanItemKeysRef.current.clear()
    setSelectedPlanItems([])
  }, [])

  const isInPlan = useCallback((key: string) => selectedPlanItemKeysRef.current.has(key.trim()), [])

  const value = useMemo(
    () => ({ selectedPlanItems, addToPlan, removeFromPlan, clearPlanItems, isInPlan }),
    [selectedPlanItems, addToPlan, removeFromPlan, clearPlanItems, isInPlan],
  )

  return <SoapNotePlanContext.Provider value={value}>{children}</SoapNotePlanContext.Provider>
}

/**
 * Hook for components that want to add text to the SOAP Note Plan field.
 * Returns null when not inside a call page (no SOAP Note available).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSoapNotePlan() {
  return useContext(SoapNotePlanContext)
}
